import { PayloadAction } from '@reduxjs/toolkit';
import { PlanboardState } from '@reducers/v2/planboard/types';
import type { OperationSearchItem } from '@reducers/v2/planboard/types';

export const onRescheduledProductionOperationsAction = (
  state: PlanboardState,
  action: PayloadAction<{
    requestCorrelationId?: string | null;
    scheduledProductionOperations?: Array<{
      productionOperationId: number;
      machineId: number;
      scheduledStartDate?: string | null;
      expectedStartOn?: string | null;
      expectedEndOn?: string | null;
      hasPriorityOverride?: boolean | null;
      scheduledStartDateOverride?: string | null;
    }>;
  }>
) => {
  const { requestCorrelationId, scheduledProductionOperations } = action.payload;
  const updates = scheduledProductionOperations ?? [];

  if (!updates.length) {
    return;
  }

  // Skip updates for machines that are being rescheduled (reset priority).
  // During reset, late-arriving onRescheduledProductionOperations batches can
  // overwrite fresh data from the refetch triggered by onFinishedReschedulingProductionOperations.
  const reschedulingMachines = new Set(state.reschedulingMachineIds);

  // chekc each operation against our pending operations
  const operationsToSkip = new Set<number>();
  const operationsToRemoveFromPending = new Set<number>();

  updates.forEach((update) => {
    const pendingOp = state.pendingOperations[update.productionOperationId];

    if (pendingOp) {
      // we're tracking this operation
      if (requestCorrelationId && pendingOp.ignoredCorrelationIds.includes(requestCorrelationId)) {
        // this is a stale event from a previous drag - skip this operation
        operationsToSkip.add(update.productionOperationId);
        return;
      }

      const isOurEvent = !requestCorrelationId || requestCorrelationId === pendingOp.correlationId;

      if (!isOurEvent && pendingOp.isOwnDrag) {
        // handle another user moved our pending card
        state.externalMoveDetected = { operationId: update.productionOperationId };
        operationsToRemoveFromPending.add(update.productionOperationId);
      } else if (!requestCorrelationId && pendingOp.isOwnDrag) {
        // Backend reschedule job (no correlationId) affecting our pending drag
        state.backendRescheduleDetected = { operationId: update.productionOperationId };
        operationsToRemoveFromPending.add(update.productionOperationId);
      } else if (isOurEvent) {
        // our own request succeeded - don't remove yet, let the 20s timeout handle cleanup
        // so that buildColumnsFromStore can preserve the optimistic position through refetch
      }
    }
  });

  // Filter out operations that should be skipped (stale events)
  const filteredUpdates = updates.filter(
    (u) => !operationsToSkip.has(u.productionOperationId) && !reschedulingMachines.has(u.machineId)
  );

  if (!filteredUpdates.length) {
    return;
  }

  const rescheduledIds = new Set(filteredUpdates.map((u) => u.productionOperationId));

  const findAndRemoveItem = (productionOperationId: number): OperationSearchItem | null => {
    for (const [columnId, column] of Object.entries(state.machineProductionOperations)) {
      const idx = column.items.findIndex((item) => item.productionOperationId === productionOperationId);
      if (idx !== -1) {
        const [item] = column.items.splice(idx, 1);
        column.total = column.items.length;
        return item;
      }
    }
    return null;
  };

  const grouped: Record<number, OperationSearchItem[]> = {};

  filteredUpdates.forEach((update) => {
    const existing = findAndRemoveItem(update.productionOperationId);
    if (!existing) {
      return;
    }

    const updatedItem: OperationSearchItem = {
      ...existing,
      scheduledStartDate: update.scheduledStartDate ?? existing.scheduledStartDate,
      expectedStartOn: update.expectedStartOn ?? existing.expectedStartOn,
      expectedEndOn: update.expectedEndOn ?? existing.expectedEndOn,
      scheduledOnMachineId: update.machineId,
      hasPriorityOverride: update.hasPriorityOverride ?? existing.hasPriorityOverride,
      scheduledStartDateOverride: update.scheduledStartDateOverride ?? existing.scheduledStartDateOverride,
    };

    if (!grouped[update.machineId]) {
      grouped[update.machineId] = [];
    }
    grouped[update.machineId].push(updatedItem);
  });

  Object.entries(grouped).forEach(([machineIdStr, items]) => {
    const machineId = Number(machineIdStr);
    const column = state.machineProductionOperations[machineId] ?? { items: [], loading: false, total: 0 };
    const remaining = column.items.filter((item) => !rescheduledIds.has(item.productionOperationId));
    const merged = items.concat(remaining).sort((a, b) => {
      const dateA = a.expectedStartOn ?? a.scheduledStartDate ?? '';
      const dateB = b.expectedStartOn ?? b.scheduledStartDate ?? '';
      return dateA.localeCompare(dateB);
    });
    state.machineProductionOperations[machineId] = { ...column, items: merged, total: merged.length };
  });

  Object.values(state.machineProductionOperations).forEach((column) => {
    column.total = column.items.length;
  });

  // remove processed operations from pending
  operationsToRemoveFromPending.forEach((opId) => {
    delete state.pendingOperations[opId];
  });
};
